import * as React from "react";
import {
  Box,
  Divider,
  List,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import EmptyState from "./EmptyState";

export interface DataListColumn {
  label: string;
  width?: number | string;
  align?: "left" | "center" | "right";
  flex?: number;
}

export interface DataListProps<T = any> {
  columns: DataListColumn[];
  data: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  isLoading?: boolean;
  emptyText?: string;
  maxHeight?: number | string;
  showDivider?: boolean;
  headerBorderColor?: string;
  sx?: any;
}

const StyledHeaderBox = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  height: 56,
  padding: theme.spacing(2, 2.5),
  borderBottomColor: "#F4A259",
  borderBottomStyle: "solid",
  borderBottomWidth: 2,
  backgroundColor: theme.palette.mode === "dark"
    ? "rgba(255, 255, 255, 0.05)"
    : "rgba(0, 0, 0, 0.03)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  [theme.breakpoints.down("sm")]: {
    height: 36,
    padding: theme.spacing(0.75, 1),
    minWidth: 650,
  },
}));

const StyledListContainer = styled(Box)(({ theme }) => ({
  maxHeight: 600,
  overflow: "auto",
  WebkitOverflowScrolling: "touch",
  "&::-webkit-scrollbar": {
    width: 8,
    height: 8,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.05)"
      : "rgba(0, 0, 0, 0.05)",
    borderRadius: 4,
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.2)"
      : "rgba(0, 0, 0, 0.2)",
    borderRadius: 4,
    "&:hover": {
      backgroundColor: theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.3)"
        : "rgba(0, 0, 0, 0.3)",
    },
  },
}));

const DataList = <T extends any = any>({
  columns,
  data,
  renderItem,
  isLoading = false,
  emptyText = "No data available",
  maxHeight = 600,
  showDivider = true,
  headerBorderColor,
  sx,
}: DataListProps<T>) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (isLoading) {
    return (
      <Box sx={{ textAlign: "center", py: 4, ...sx }}>
        <EmptyState type="noData" message="Loading..." />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box sx={{ py: 4, ...sx }}>
        <EmptyState type="noData" message={emptyText} />
      </Box>
    );
  }

  return (
    <Box sx={sx}>
      <StyledHeaderBox
        sx={{
          borderBottomColor: headerBorderColor || "#F4A259",
        }}
      >
        {columns.map((column, index) => (
          <Box
            key={index}
            sx={{
              flex: column.flex || (typeof column.width === "number" ? 0 : 1),
              width: typeof column.width === "number" ? column.width : column.width,
              display: "flex",
              alignItems: "center",
              justifyContent:
                column.align === "center"
                  ? "center"
                  : column.align === "right"
                    ? "flex-end"
                    : "flex-start",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: { xs: "0.6rem", sm: 13 },
                fontWeight: 700,
                color: "text.primary",
                textTransform: "uppercase",
                letterSpacing: { xs: "0.3px", sm: "0.8px" },
                whiteSpace: { xs: "nowrap", sm: "normal" },
              }}
            >
              {column.label}
            </Typography>
          </Box>
        ))}
      </StyledHeaderBox>
      <StyledListContainer sx={{ maxHeight }}>
        <List sx={{ py: 0 }}>
          {data.map((item, index) => (
            <React.Fragment key={index}>
              {renderItem(item, index)}
              {showDivider && index < data.length - 1 && (
                <Divider component="li" />
              )}
            </React.Fragment>
          ))}
        </List>
      </StyledListContainer>
    </Box>
  );
};

export default DataList;

